import { AsyncBoundary } from "@base/ui/AsyncBoundary";
import { Button } from "@base/ui/Button/Button";
import { Dialog } from "@base/ui/Dialog";
import { TextField } from "@base/ui/TextField";
import { membershipQueries } from "@features/membership/api/queryFactory";
import { formatPlanDisplay } from "@features/membership/lib/utils";
import { useUpgradeMembershipPlanPayment } from "@features/membership/model/hooks";
import type { MembershipPlanType } from "@features/membership/model/types";
import EligiblePlanSelector from "@features/membership/ui/EligiblePlanSelector";
import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import { useForm } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

function SubmitButton({ eligiblePlan, isPending }: { eligiblePlan: MembershipPlanType | ""; isPending: boolean }) {
  const { t } = useTranslation();
  const { data: membership } = useQuery(membershipQueries.membership());
  const { data: upgradeCalculation } = useQuery({
    ...membershipQueries.upgradeCalculation(eligiblePlan as MembershipPlanType),
    enabled: !!eligiblePlan && !!membership,
  });

  return (
    <Button type="submit" className="w-full" disabled={!eligiblePlan || isPending}>
      {t("payAmount", { amount: Number.parseFloat(upgradeCalculation?.charged_amount ?? "0").toFixed(2) })}
    </Button>
  );
}

function AdditionalPaymentSummary({ eligiblePlan }: { eligiblePlan: MembershipPlanType }) {
  const { t } = useTranslation();

  const { data: membership } = useQuery(membershipQueries.membership());
  const { data: upgradeCalculation } = useQuery({
    ...membershipQueries.upgradeCalculation(eligiblePlan),
    enabled: !!eligiblePlan && !!membership,
  });

  const remainingDays = dayjs(upgradeCalculation?.cycle_end_date).diff(dayjs(upgradeCalculation?.today), "day") + 1;

  const listItems: { key: string; text: string }[] = [
    {
      key: "remaining-days",
      text: t("remainingDaysLabel", {
        start: upgradeCalculation?.today,
        end: upgradeCalculation?.cycle_end_date,
        days: remainingDays,
      }),
    },
    {
      key: "original-paid",
      text: t("originalPlanPaidAmount", {
        full: upgradeCalculation?.original_plan_full_amount,
        days: remainingDays,
        cycleDays: upgradeCalculation?.cycle_range_days,
        remaining: Number.parseFloat(upgradeCalculation?.original_plan_remaining_amount ?? "0").toFixed(2),
      }),
    },
    {
      key: "new-prorated",
      text: t("newPlanProratedAmount", {
        amount: upgradeCalculation?.upgrade_plan_full_amount,
        cycleDays: upgradeCalculation?.cycle_range_days,
        prorated: Number.parseFloat(upgradeCalculation?.upgrade_plan_prorated_amount ?? "0").toFixed(2),
      }),
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <h4 className="font-semibold text-label1 text-gray-1000">{t("additionalPaymentSummary")}</h4>
      <ul className="flex flex-col gap-1 pl-0 list-none text-gray-900 text-label2">
        {listItems.map(({ key, text }) => (
          <li key={key} className="flex gap-2">
            <span className="shrink-0">•</span>
            <span>{text}</span>
          </li>
        ))}
        <li className="flex gap-2 font-semibold text-gray-1000">
          <span className="shrink-0">•</span>
          <span>
            {t("chargedAmount", {
              charged: Number.parseFloat(upgradeCalculation?.charged_amount ?? "0").toFixed(2),
            })}
          </span>
        </li>
      </ul>
    </div>
  );
}

const UpgradePlanDialog = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation();

  const { data: membership } = useQuery(membershipQueries.membership());

  const originalPlanDisplay = membership?.plan_description
    ? formatPlanDisplay(membership?.plan_description.type, membership?.plan_description.billing_cycle, t)
    : "";

  const { data: eligiblePlans } = useQuery({
    ...membershipQueries.eligiblePlans(),
    enabled: !!membership,
  });

  const eligiblePlansList = eligiblePlans?.results ?? [];

  const { mutate, isPending } = useUpgradeMembershipPlanPayment();

  const form = useForm({
    defaultValues: {
      eligiblePlan: "",
    },
    onSubmit: async () => {
      if (isPending) return;

      mutate(form.state.values.eligiblePlan as MembershipPlanType, {
        onError: () => {
          toast.error(t("upgradePlanFailed"));
        },
        onSettled: () => {
          onClose();
        },
      });
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <Dialog.Content className="w-full max-w-[calc(100vw-32px)] md:max-w-[455px]">
        <div className="flex justify-between items-center p-4 pb-0">
          <Dialog.Title>{t("upgradePlan")}</Dialog.Title>
          <Dialog.CloseTrigger asChild>
            <button type="button" className="p-1 -m-1 hover:bg-gray-100 rounded-4">
              <Icon path={mdiClose} size="1.5rem" />
            </button>
          </Dialog.CloseTrigger>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
          className="flex flex-col gap-4 p-4"
        >
          <TextField disabled className="flex flex-col gap-1">
            <TextField.Label>{t("originalPlan")}</TextField.Label>
            <TextField.Input value={originalPlanDisplay} readOnly />
          </TextField>

          <form.Field name="eligiblePlan">
            {(field) => (
              <EligiblePlanSelector
                items={eligiblePlansList}
                value={field.state.value}
                onValueChange={field.handleChange}
                label={t("eligiblePlan")}
                placeholder={t("selectPlan")}
                required
              />
            )}
          </form.Field>

          <form.Subscribe selector={(state) => state.values.eligiblePlan}>
            {(eligiblePlan) =>
              eligiblePlan ? (
                <AsyncBoundary>
                  <AdditionalPaymentSummary eligiblePlan={eligiblePlan as MembershipPlanType} />
                </AsyncBoundary>
              ) : null
            }
          </form.Subscribe>

          <form.Subscribe selector={(state) => state.values.eligiblePlan}>
            {(eligiblePlan) => (
              <SubmitButton eligiblePlan={eligiblePlan as MembershipPlanType | ""} isPending={isPending} />
            )}
          </form.Subscribe>
        </form>
      </Dialog.Content>
    </Dialog>
  );
};

export default UpgradePlanDialog;
